'use client'

import { useRef, useMemo, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, ArrowUpDown, ArrowUp, ArrowDown, Trash2 } from 'lucide-react'
import { AssigneeCell, PriorityTag, StatusTag, shortId, relativeTime } from '@/components/issues/issue-table-parts'
import { useTranslations } from '@/lib/i18n'

interface Issue {
  id: number
  number?: number
  subject: string
  description: string
  tracker: { id: number; name: string }
  status: { id: number; name: string; is_closed: boolean }
  priority: { id: number; name: string }
  author: { id: number; name: string }
  assigned_to?: { id: number; name: string }
  category?: { id: number; name: string }
  fixed_version?: { id: number; name: string }
  parent?: { id: number; subject: string }
  children?: Issue[]
  project: { id: number; name: string }
  start_date?: string
  due_date?: string
  done_ratio: number
  estimated_hours?: number
  spent_hours?: number
  created_on: string
  updated_on: string
}

interface VirtualizedIssuesTableProps {
  issues: Issue[]
  shouldShowProjectName: boolean
  onOpenMenu: (issue: Issue) => void
  onDelete?: (issue: Issue) => void
}

type SortColumn = 'id' | 'subject' | 'priority' | 'status' | 'assignee' | 'updated'

/**
 * Virtualized Issues Table — div-based layout so only the visible rows render.
 * Visually matches the inline table in issues-list-view; used once a list has
 * 50+ issues for smooth scrolling.
 */
export function VirtualizedIssuesTable({
  issues,
  shouldShowProjectName,
  onOpenMenu,
  onDelete,
}: VirtualizedIssuesTableProps) {
  const t = useTranslations('issuesPage')
  const parentRef = useRef<HTMLDivElement>(null)

  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const sortedIssues = useMemo(() => {
    if (!sortColumn) return issues
    return [...issues].sort((a, b) => {
      let aValue: any
      let bValue: any
      switch (sortColumn) {
        case 'id':
          aValue = a.id; bValue = b.id; break
        case 'subject':
          aValue = a.subject.toLowerCase(); bValue = b.subject.toLowerCase(); break
        case 'priority':
          aValue = a.priority?.name?.toLowerCase() || ''
          bValue = b.priority?.name?.toLowerCase() || ''
          break
        case 'status':
          aValue = a.status?.name.toLowerCase() || ''
          bValue = b.status?.name.toLowerCase() || ''
          break
        case 'assignee':
          aValue = a.assigned_to?.name.toLowerCase() || 'zzz'
          bValue = b.assigned_to?.name.toLowerCase() || 'zzz'
          break
        case 'updated':
          aValue = new Date(a.updated_on).getTime() || 0
          bValue = new Date(b.updated_on).getTime() || 0
          break
        default:
          return 0
      }
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [issues, sortColumn, sortDirection])

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-0 transition-opacity group-hover:opacity-50" />
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 inline h-3 w-3" />
      : <ArrowDown className="ml-1 inline h-3 w-3" />
  }

  const rowHeight = shouldShowProjectName ? 72 : 56

  const virtualizer = useVirtualizer({
    count: sortedIssues.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  })

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      {/* Header */}
      <div className="flex items-center border-b bg-muted/40 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <button
          className="group flex w-20 items-center px-4 py-2.5 text-left transition-colors hover:text-foreground"
          onClick={() => handleSort('id')}
        >
          {t('tableHeaderId')}<SortIcon column="id" />
        </button>
        <button
          className="group flex flex-1 items-center px-4 py-2.5 text-left transition-colors hover:text-foreground"
          onClick={() => handleSort('subject')}
        >
          {t('tableHeaderSubject')}<SortIcon column="subject" />
        </button>
        <button
          className="group hidden w-32 items-center px-4 py-2.5 text-left transition-colors hover:text-foreground lg:flex"
          onClick={() => handleSort('priority')}
        >
          {t('fields.priority')}<SortIcon column="priority" />
        </button>
        <button
          className="group flex w-32 items-center px-4 py-2.5 text-left transition-colors hover:text-foreground"
          onClick={() => handleSort('status')}
        >
          {t('tableHeaderStatus')}<SortIcon column="status" />
        </button>
        <button
          className="group hidden w-48 items-center px-4 py-2.5 text-left transition-colors hover:text-foreground md:flex"
          onClick={() => handleSort('assignee')}
        >
          {t('tableHeaderAssignee')}<SortIcon column="assignee" />
        </button>
        <button
          className="group hidden w-28 items-center px-4 py-2.5 text-left transition-colors hover:text-foreground lg:flex"
          onClick={() => handleSort('updated')}
        >
          {t('tableHeaderUpdated')}<SortIcon column="updated" />
        </button>
        <div className="w-24 px-4 py-2.5" />
      </div>

      {/* Scrollable virtualized body */}
      <div ref={parentRef} className="overflow-auto" style={{ height: '600px', contain: 'strict' }}>
        <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const issue = sortedIssues[virtualRow.index]
            return (
              <div
                key={issue.id}
                className="group absolute left-0 flex w-full items-center border-b border-border transition-colors hover:bg-muted/40"
                style={{ height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)` }}
              >
                <div className="w-20 px-4">
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                    #{issue.number ?? shortId(issue.id)}
                  </span>
                </div>
                <div className="min-w-0 flex-1 px-4">
                  <Link href={`/issues/${issue.id}`} className="block">
                    <div className="flex items-center gap-1 min-w-0">
                      {issue.tracker?.name && (
                        <span className="shrink-0 text-xs text-muted-foreground">{issue.tracker.name} ·</span>
                      )}
                      <span className="truncate font-medium text-foreground transition-colors group-hover:text-primary">
                        {issue.subject}
                      </span>
                    </div>
                    {shouldShowProjectName && issue.project?.name && (
                      <div className="mt-0.5 text-[11px] text-muted-foreground truncate">
                        {issue.project.name}
                      </div>
                    )}
                  </Link>
                </div>
                <div className="hidden w-32 px-4 lg:block">
                  <PriorityTag name={issue.priority?.name} />
                </div>
                <div className="w-32 px-4">
                  <StatusTag name={issue.status?.name} isClosed={issue.status?.is_closed} />
                </div>
                <div className="hidden w-48 px-4 md:block">
                  <AssigneeCell name={issue.assigned_to?.name} />
                </div>
                <div className="hidden w-28 whitespace-nowrap px-4 text-xs text-muted-foreground lg:block">
                  {relativeTime(issue.updated_on) || '—'}
                </div>
                <div className="flex w-24 items-center justify-end gap-0.5 px-4">
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onDelete(issue)
                      }}
                      title={t('deleteIssue')}
                      aria-label={t('deleteIssue')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onOpenMenu(issue)
                    }}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
